import React, { useState, useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { 
  FiSave, 
  FiInfo,
  FiPlus, 
  FiTrash2, 
  FiBarChart,
  FiImage
} from 'react-icons/fi'
import { useContent } from '../../../hooks/useContent'
import { AboutContent, VenueLogo } from '../../../types/content'
import MediaPicker from '../MediaPicker'
import toast from 'react-hot-toast'

interface AboutEditorProps {
  onPreview?: (data: AboutContent) => void
}

const AboutEditor: React.FC<AboutEditorProps> = ({ onPreview }) => {
  const { content, loading, saving, saveContent } = useContent('about')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showMediaPicker, setShowMediaPicker] = useState(false)
  const [selectedVenueIndex, setSelectedVenueIndex] = useState<number | null>(null)
  const [venues, setVenues] = useState<VenueLogo[]>([])

  const { register, control, handleSubmit, watch, reset, formState: { errors } } = useForm<AboutContent>({
    defaultValues: {
      title: '',
      subtitle: '',
      description: '',
      ctaButtonText: '',
      stats: [
        { number: '', label: '' }
      ],
      venueLogos: []
    }
  })

  const { fields: statsFields, append: appendStat, remove: removeStat } = useFieldArray({
    control,
    name: 'stats'
  })

  const watchedData = watch()

  // Default descriptions for venues (fallback)
  const defaultDescriptions: Record<string, string> = {
    'Gaucho': 'We provide exceptional musical entertainment for this prestigious venue, ensuring every performance meets the highest standards.',
    'The Municipal Hotel': 'Our curated musicians deliver unforgettable performances at luxury hotels, creating magical moments for guests and visitors.',
    'Manchester City FC': 'From intimate corporate events to major celebrations, we bring the perfect sound to sporting venues and stadiums across the country.',
    'Madison Square Garden': 'We provide world-class musical entertainment for the most prestigious events at iconic venues like Madison Square Garden, ensuring every performance meets the highest standards.',
    'Hollywood Bowl': 'Our curated musicians deliver unforgettable performances at outdoor amphitheaters, creating magical moments under the stars for audiences of all sizes.',
    'Red Rocks': 'From intimate acoustic sets to full band performances, we bring the perfect sound to natural amphitheaters and unique outdoor venues across the country.'
  }

  // Load content when available
  useEffect(() => {
    if (content?.data) {
      const aboutData = content.data as AboutContent
      reset(aboutData)
      
      // Populate venues with default descriptions if they don't have one
      const venuesWithDescriptions = (aboutData.venueLogos || []).map(venue => ({
        ...venue,
        description: venue.description || defaultDescriptions[venue.name] || ''
      }))
      
      setVenues(venuesWithDescriptions)
      setHasUnsavedChanges(false)
    }
  }, [content, reset])

  // Watch for changes (including venues)
  useEffect(() => {
    if (content?.data) {
      const currentData = JSON.stringify({ ...watchedData, venueLogos: venues })
      const originalData = JSON.stringify(content.data)
      setHasUnsavedChanges(currentData !== originalData)
    }
  }, [watchedData, venues, content])

  // Auto-preview
  useEffect(() => {
    if (hasUnsavedChanges && onPreview) {
      onPreview(watchedData)
    }
  }, [watchedData, hasUnsavedChanges, onPreview])

  const onSubmit = async (data: AboutContent) => {
    const dataWithVenues = {
      ...data,
      venueLogos: venues
    }
    
    
    const success = await saveContent(dataWithVenues)
    if (success) {
      setHasUnsavedChanges(false)
    }
  }

  const handleAddVenue = () => {
    const newVenue: VenueLogo = {
      id: `venue_${Date.now()}`,
      name: '',
      logo: '',
      description: 'We provide exceptional musical entertainment for this prestigious venue, ensuring every performance meets the highest standards.',
      website: '',
      order: venues.length
    }
    setVenues([...venues, newVenue])
  }

  const handleUpdateVenue = (index: number, updates: Partial<VenueLogo>) => {
    const updatedVenues = [...venues]
    updatedVenues[index] = { ...updatedVenues[index], ...updates }
    setVenues(updatedVenues)
  }

  const handleRemoveVenue = (index: number) => {
    setVenues(venues.filter((_, i) => i !== index))
  }

  const handleSelectVenueLogo = (index: number) => {
    setSelectedVenueIndex(index)
    setShowMediaPicker(true)
  }

  const handleAddStat = () => {
    appendStat({ number: '', label: '' })
  }

  const handleRemoveStat = (index: number) => {
    if (statsFields.length > 1) {
      removeStat(index)
    } else {
      toast.error('You must have at least one stat')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-neutral-600">Loading about content...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Save Button Header */}
      <div className="flex items-center justify-end space-x-3">
        {hasUnsavedChanges && (
          <span className="text-sm text-amber-600 font-medium">
            Unsaved changes
          </span>
        )}
        
        <button
          onClick={handleSubmit(onSubmit)}
          disabled={saving || !hasUnsavedChanges}
          className={`flex items-center space-x-2 px-5 py-2.5 rounded-lg transition-all text-sm font-medium ${
            hasUnsavedChanges && !saving
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-md'
              : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
          }`}
        >
          {saving ? (
            <>
              <div className="loading-spinner w-4 h-4"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <FiSave size={16} />
              <span>Save Changes</span>
            </>
          )}
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Main Content */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-800 mb-6 flex items-center space-x-2">
            <FiInfo size={18} />
            <span>About Content</span>
          </h3>
          
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Section Title
              </label>
              <input
                {...register('title', { required: 'Title is required' })}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="Perfect Music for"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
              )}
            </div>

            {/* Subtitle */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Subtitle (Highlighted Text)
              </label>
              <input
                {...register('subtitle', { required: 'Subtitle is required' })}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="Every Occasion"
              />
              {errors.subtitle && (
                <p className="mt-1 text-sm text-red-600">{errors.subtitle.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Description
              </label>
              <textarea
                {...register('description', { required: 'Description is required' })}
                rows={4}
                className="w-full px-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 resize-vertical"
                placeholder="Describe your company's mission, history, and what makes you unique..."
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>

            {/* CTA Button Text */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Call-to-Action Button Text
              </label>
              <input
                {...register('ctaButtonText', { required: 'Button text is required' })}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition-all text-sm"
                placeholder="Book Live Music"
              />
              {errors.ctaButtonText && (
                <p className="mt-1 text-sm text-red-600">{errors.ctaButtonText.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
              <FiBarChart size={18} />
              <span>Statistics</span>
            </h3>
            
            <button
              type="button"
              onClick={handleAddStat}
              className="flex items-center space-x-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200"
            >
              <FiPlus size={16} />
              <span>Add Stat</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {statsFields.map((field, index) => (
              <div
                key={field.id}
                className="p-4 border border-neutral-200 rounded-xl space-y-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-neutral-700">
                    Stat {index + 1}
                  </span>
                  {statsFields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveStat(index)}
                      className="text-red-500 hover:text-red-700 transition-colors duration-200"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  )}
                </div>
                
                <div className="space-y-2">
                  <input
                    {...register(`stats.${index}.number` as const, { required: 'Number is required' })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                    placeholder="500+"
                  />
                  <input
                    {...register(`stats.${index}.label` as const, { required: 'Label is required' })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                    placeholder="Events Booked"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Venue Logos */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
              <FiImage size={18} />
              <span>Logo Carousel</span>
            </h3>
            
            <button
              type="button"
              onClick={handleAddVenue}
              className="flex items-center space-x-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200"
            >
              <FiPlus size={16} />
              <span>Add Venue</span>
            </button>
          </div>

          <div className="space-y-4">
            {venues.map((venue, index) => (
              <div
                key={venue.id}
                className="p-4 border border-neutral-200 rounded-xl space-y-4"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-neutral-700">
                    Venue {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveVenue(index)}
                    className="text-red-500 hover:text-red-700 transition-colors duration-200"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Venue Name */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Venue Name
                    </label>
                    <input
                      value={venue.name}
                      onChange={(e) => handleUpdateVenue(index, { name: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      placeholder="e.g., Madison Square Garden"
                    />
                  </div>

                  {/* Website (Optional) */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Website (Optional)
                    </label>
                    <input
                      value={venue.website || ''}
                      onChange={(e) => handleUpdateVenue(index, { website: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      placeholder="https://venue-website.com"
                    />
                  </div>

                  {/* Description */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={venue.description || ''}
                      onChange={(e) => handleUpdateVenue(index, { description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 resize-vertical"
                      placeholder="Describe your partnership with this venue and the type of entertainment you provide..."
                    />
                  </div>

                  {/* Logo Section */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Venue Logo
                    </label>
                    
                    <div className="space-y-3">
                      {/* Logo Preview */}
                      {venue.logo && (
                        <div className="w-48 h-20 bg-white border border-neutral-200 rounded-lg p-2 flex items-center justify-center">
                          <img
                            src={venue.logo}
                            alt={venue.name}
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      )}

                      {/* Logo URL Input with Browse Button */}
                      <div className="flex space-x-3">
                        <input
                          value={venue.logo}
                          onChange={(e) => handleUpdateVenue(index, { logo: e.target.value })}
                          className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                          placeholder="Logo URL or click Browse"
                        />
                        <button
                          type="button"
                          onClick={() => handleSelectVenueLogo(index)}
                          className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors duration-200 flex items-center space-x-2"
                        >
                          <FiImage size={16} />
                          <span>Browse</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {venues.length === 0 && (
              <div className="text-center py-8 text-neutral-500">
                <FiImage size={32} className="mx-auto mb-2" />
                <p>No venue logos added yet</p>
                <button
                  type="button"
                  onClick={handleAddVenue}
                  className="mt-2 text-primary hover:text-secondary transition-colors text-sm"
                >
                  Add your first venue logo
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Save Button (Mobile) */}
        <div className="lg:hidden">
          <button
            type="submit"
            disabled={saving || !hasUnsavedChanges}
            className={`w-full flex items-center justify-center space-x-2 px-6 py-3 rounded-xl transition-all duration-200 ${
              hasUnsavedChanges && !saving
                ? 'bg-primary text-white hover:bg-primary/90'
                : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <div className="loading-spinner w-4 h-4"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FiSave size={16} />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Media Picker Modal */}
      <MediaPicker
        isOpen={showMediaPicker}
        onClose={() => {
          setShowMediaPicker(false)
          setSelectedVenueIndex(null)
        }}
        onSelect={(imageUrl) => {
          if (selectedVenueIndex !== null) {
            handleUpdateVenue(selectedVenueIndex, { logo: imageUrl })
          }
          setShowMediaPicker(false)
          setSelectedVenueIndex(null)
        }}
        selectedUrl={selectedVenueIndex !== null ? venues[selectedVenueIndex]?.logo : undefined}
      />
    </div>
  )
}

export default AboutEditor
